import React, { useState, useEffect } from 'react';
import {
  Row, Col, Card, Table, Tag, Typography, Space, Button, Modal, Form,
  Input, Select, message, Popconfirm, Badge, Radio, Divider
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, KeyOutlined, UserOutlined } from '@ant-design/icons';
import { getUsers, createUser, updateUser, deleteUser, resetPassword, getEvents } from '../api';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

const PRIMARY = '#2563eb';
const SECONDARY = '#1e293b';
const ROLE_COLOR = { admin: 'red', operator: 'blue', viewer: 'default' };

const MODULES = [
  { label: 'Live Tracking',       key: 'perm_live_tracking'       },
  { label: 'Event Dashboard',     key: 'perm_event_dashboard'     },
  { label: 'Delegate Dashboard',  key: 'perm_delegate_dashboard'  },
  { label: 'Reader Dashboard',    key: 'perm_reader_dashboard'    },
  { label: 'Report Master',       key: 'perm_report_master'       },
  { label: 'Category Master',     key: 'perm_category_master'     },
  { label: 'Location Master',     key: 'perm_location_master'     },
  { label: 'Reader Manager',      key: 'perm_reader_manager'      },
  { label: 'Handheld Simulator',  key: 'perm_handheld_simulator'  },
  { label: 'Raw Inventory',       key: 'perm_raw_inventory'       },
  { label: 'Welcome Screen',      key: 'perm_welcome_screen'      },
  { label: 'Reader API',          key: 'perm_reader_api'          },
  { label: 'Client API Push',     key: 'perm_client_push'         },
  { label: 'DB Config',           key: 'perm_db_config'           },
  { label: 'User Management',     key: 'perm_user_management'     },
];

const ROLE_DEFAULTS = {
  admin: Object.fromEntries(MODULES.map(m => [m.key, 'edit'])),
  operator: {
    perm_live_tracking: 'view', perm_event_dashboard: 'view', perm_delegate_dashboard: 'edit',
    perm_reader_dashboard: 'view', perm_report_master: 'view',
    perm_category_master: 'none', perm_location_master: 'none', perm_reader_manager: 'none',
    perm_handheld_simulator: 'none', perm_raw_inventory: 'none', perm_welcome_screen: 'none',
    perm_reader_api: 'none', perm_client_push: 'none', perm_db_config: 'none', perm_user_management: 'none',
  },
  viewer: {
    perm_live_tracking: 'view', perm_event_dashboard: 'view', perm_delegate_dashboard: 'view',
    perm_reader_dashboard: 'view', perm_report_master: 'view',
    perm_category_master: 'view', perm_location_master: 'view', perm_reader_manager: 'view',
    perm_handheld_simulator: 'view', perm_raw_inventory: 'view', perm_welcome_screen: 'view',
    perm_reader_api: 'none', perm_client_push: 'none', perm_db_config: 'none', perm_user_management: 'none',
  },
};

function PermissionSummary({ record }) {
  const counts = { edit: 0, view: 0, none: 0 };
  MODULES.forEach(({ key }) => {
    const val = (record[key] || 'none').toLowerCase();
    if (val in counts) counts[val]++; else counts.none++;
  });
  const parts = [];
  if (counts.edit > 0) parts.push(<Tag color={PRIMARY} key="edit" style={{ marginRight: 2 }}>{counts.edit} Edit</Tag>);
  if (counts.view > 0) parts.push(<Tag color="cyan" key="view" style={{ marginRight: 2 }}>{counts.view} View</Tag>);
  if (counts.none > 0) parts.push(<Tag color="default" key="none" style={{ marginRight: 2 }}>{counts.none} None</Tag>);
  return <span>{parts}</span>;
}

function ModulePermissions() {
  const left  = MODULES.slice(0, Math.ceil(MODULES.length / 2));
  const right = MODULES.slice(Math.ceil(MODULES.length / 2));

  const renderColumn = (modules) =>
    modules.map(({ label, key }) => (
      <div key={key} style={{ display: 'flex', alignItems: 'center', marginBottom: 8, gap: 8 }}>
        <span style={{ width: 150, flexShrink: 0, fontSize: 12, color: SECONDARY, fontWeight: 500 }}>{label}</span>
        <Form.Item name={key} noStyle initialValue="none">
          <Radio.Group size="small" buttonStyle="solid">
            <Radio.Button value="none" style={{ fontSize: 11, padding: '0 8px' }}>None</Radio.Button>
            <Radio.Button value="view" style={{ fontSize: 11, padding: '0 8px' }}>View</Radio.Button>
            <Radio.Button value="edit" style={{ fontSize: 11, padding: '0 8px' }}>Edit</Radio.Button>
          </Radio.Group>
        </Form.Item>
      </div>
    ));

  return (
    <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, padding: '12px 16px 4px', marginBottom: 16, background: '#fafafa' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: PRIMARY, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Module Access Permissions
      </div>
      <div style={{ display: 'flex', gap: 24 }}>
        <div style={{ flex: 1 }}>{renderColumn(left)}</div>
        <div style={{ flex: 1 }}>{renderColumn(right)}</div>
      </div>
    </div>
  );
}

export default function UserManagement() {
  const [data, setData]             = useState([]);
  const [events, setEvents]         = useState([]);
  const [loading, setLoading]       = useState(false);
  const [modal, setModal]           = useState(false);
  const [pwdModal, setPwdModal]     = useState(false);
  const [editing, setEditing]       = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [form]    = Form.useForm();
  const [pwdForm] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try { const r = await getUsers(); setData(r.data.data || []); } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    getEvents().then(r => setEvents(r.data.data || [])).catch(() => {});
  }, []);

  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ role: 'operator', event_access: [], ...ROLE_DEFAULTS.operator });
    setModal(true);
  };

  const openEdit = (rec) => {
    setEditing(rec);
    const permValues = {};
    MODULES.forEach(({ key }) => { permValues[key] = rec[key] || 'none'; });
    form.setFieldsValue({ ...rec, password: '', event_access: rec.event_access || [], ...permValues });
    setModal(true);
  };

  const openPwd = (rec) => {
    setSelectedUser(rec);
    pwdForm.resetFields();
    setPwdModal(true);
  };

  const handleRoleChange = (role) => {
    const defaults = ROLE_DEFAULTS[role] || {};
    form.setFieldsValue(defaults);
  };

  const handleSave = async (values) => {
    try {
      if (editing) { await updateUser(editing.id, values); message.success('User updated'); }
      else         { await createUser(values);              message.success('User created'); }
      setModal(false); load();
    } catch (err) { message.error(err.response?.data?.message || 'Error'); }
  };

  const handleDelete = async (id) => {
    try { await deleteUser(id); message.success('User deactivated'); load(); }
    catch (err) { message.error(err.response?.data?.message || 'Error'); }
  };

  const handleResetPwd = async (values) => {
    try {
      await resetPassword(selectedUser.id, { new_password: values.new_password });
      message.success('Password reset');
      setPwdModal(false);
    } catch (err) { message.error(err.response?.data?.message || 'Error'); }
  };

  const exportExcel = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Users');
    ws.addRow(['#', 'Username', 'Full Name', 'Email', 'Phone', 'Role', 'Status', 'Last Login', 'Created']);
    ws.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F1D6B' } };
    ws.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
    data.forEach(u => ws.addRow([
      u.id, u.username, u.full_name, u.email || '', u.phone || '', u.role, u.status,
      u.last_login ? dayjs(u.last_login).format('DD-MMM-YYYY HH:mm') : 'Never',
      dayjs(u.created_at).format('DD-MMM-YYYY'),
    ]));
    ws.columns.forEach(c => { c.width = 20; });
    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf]), `users_${dayjs().format('YYYYMMDD')}.xlsx`);
    message.success('Excel exported');
  };

  const exportPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(14);
    doc.text('User Management Report', 14, 16);
    doc.setFontSize(9);
    doc.text(`Generated: ${dayjs().format('DD MMM YYYY HH:mm')}`, 14, 23);
    doc.autoTable({
      startY: 28,
      head: [['#', 'Username', 'Full Name', 'Email', 'Phone', 'Role', 'Status', 'Last Login']],
      body: data.map(u => [
        u.id, u.username, u.full_name, u.email || '', u.phone || '',
        u.role, u.status, u.last_login ? dayjs(u.last_login).format('DD-MMM-YY HH:mm') : 'Never',
      ]),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [15, 29, 107] },
    });
    doc.save(`users_${dayjs().format('YYYYMMDD')}.pdf`);
    message.success('PDF exported');
  };

  const columns = [
    { title: '#', dataIndex: 'id', width: 55 },
    {
      title: 'Username', dataIndex: 'username',
      render: v => <><UserOutlined style={{ marginRight: 6, color: PRIMARY }} />{v}</>,
    },
    { title: 'Full Name', dataIndex: 'full_name' },
    { title: 'Email', dataIndex: 'email', render: v => v || '-' },
    { title: 'Phone', dataIndex: 'phone', render: v => v || '-' },
    {
      title: 'Role', dataIndex: 'role', width: 90,
      render: v => <Tag color={ROLE_COLOR[v]}>{v?.toUpperCase()}</Tag>,
    },
    {
      title: 'Status', dataIndex: 'status', width: 90,
      render: v => <Badge status={v === 'active' ? 'success' : 'error'} text={v} />,
    },
    {
      title: 'Last Login', dataIndex: 'last_login', width: 140,
      render: v => v ? dayjs(v).format('DD-MMM-YY HH:mm') : <Tag>Never</Tag>,
    },
    {
      title: 'Event Access', width: 180,
      render: (_, r) => {
        const ea = r.event_access;
        if (!ea || ea.length === 0) return <Tag color="green">All Events</Tag>;
        return (
          <Space size={2} wrap>
            {ea.map(eid => {
              const ev = events.find(e => e.id === eid);
              return ev ? <Tag key={eid} color="blue" style={{ fontSize: 11 }}>{ev.event_name}</Tag> : null;
            })}
          </Space>
        );
      },
    },
    {
      title: 'Permissions', width: 200,
      render: (_, r) => <PermissionSummary record={r} />,
    },
    {
      title: 'Actions', width: 130,
      render: (_, r) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} type="link" />
          <Button size="small" icon={<KeyOutlined />} onClick={() => openPwd(r)} type="link" style={{ color: '#faad14' }} />
          <Popconfirm title="Deactivate user?" onConfirm={() => handleDelete(r.id)} okText="Yes" cancelText="No">
            <Button size="small" icon={<DeleteOutlined />} type="link" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <Title level={4} style={{ margin: 0, color: '#0F1D6B' }}>
            <UserOutlined style={{ marginRight: 8 }} />User Management
          </Title>
        </div>
        <Space>
          <Button onClick={exportExcel} style={{ background: '#1D6F42', borderColor: '#1D6F42', color: '#fff' }}>Excel</Button>
          <Button onClick={exportPDF} style={{ background: '#C41E3A', borderColor: '#C41E3A', color: '#fff' }}>PDF</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd} style={{ background: PRIMARY }}>
            Add User
          </Button>
        </Space>
      </div>

      <Card style={{ borderRadius: 10 }}>
        <Table
          columns={columns} dataSource={data} rowKey="id" loading={loading}
          pagination={{ pageSize: 15, showTotal: (t, r) => `${r[0]}-${r[1]} of ${t}` }}
          scroll={{ x: 1100 }} size="middle"
        />
      </Card>

      {/* Add / Edit User Modal */}
      <Modal
        title={editing ? 'Edit User' : 'Add New User'}
        open={modal} onCancel={() => setModal(false)} footer={null}
        width={680}
        styles={{ body: { maxHeight: '75vh', overflowY: 'auto', paddingRight: 4 } }}
      >
        <Form form={form} layout="vertical" onFinish={handleSave} style={{ marginTop: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Form.Item name="full_name" label="Full Name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="username" label="Username" rules={[{ required: true }]}>
              <Input disabled={!!editing} />
            </Form.Item>
            {!editing && (
              <Form.Item name="password" label="Password" rules={[{ required: true, min: 6 }]}>
                <Input.Password />
              </Form.Item>
            )}
            <Form.Item name="email" label="Email">
              <Input type="email" />
            </Form.Item>
            <Form.Item name="phone" label="Phone">
              <Input />
            </Form.Item>
            <Form.Item name="role" label="Role" initialValue="operator" rules={[{ required: true }]}>
              <Select onChange={handleRoleChange}>
                <Option value="admin">Admin</Option>
                <Option value="operator">Operator</Option>
                <Option value="viewer">Viewer</Option>
              </Select>
            </Form.Item>
            {editing && (
              <Form.Item name="status" label="Status" rules={[{ required: true }]}>
                <Select>
                  <Option value="active">Active</Option>
                  <Option value="inactive">Inactive</Option>
                </Select>
              </Form.Item>
            )}
          </div>

          {/* Event Access */}
          <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, padding: '12px 16px', background: '#fafafa', marginBottom: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: PRIMARY, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Event Access
            </div>
            <Form.Item
              name="event_access" style={{ marginBottom: 0 }}
              extra={<span style={{ fontSize: 11, color: '#64748b' }}>Leave empty to grant access to <strong>all events</strong>. Select specific events to restrict this user.</span>}
            >
              <Select
                mode="multiple" placeholder="All Events (no restriction)"
                allowClear style={{ width: '100%' }} optionFilterProp="label"
                options={events.map(e => ({
                  label: (
                    <Space size={6}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: e.status === 'active' ? '#10b981' : '#94a3b8', display: 'inline-block' }} />
                      {e.event_name}
                      <Tag style={{ fontSize: 10, margin: 0 }}>{e.status}</Tag>
                    </Space>
                  ),
                  value: e.id,
                }))}
              />
            </Form.Item>
          </div>

          <Divider style={{ margin: '8px 0 16px' }} />

          <ModulePermissions />

          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => setModal(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" style={{ background: PRIMARY }}>
              {editing ? 'Update' : 'Create'}
            </Button>
          </Space>
        </Form>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        title={`Reset Password — ${selectedUser?.full_name}`}
        open={pwdModal} onCancel={() => setPwdModal(false)} footer={null} width={400}
      >
        <Form form={pwdForm} layout="vertical" onFinish={handleResetPwd} style={{ marginTop: 16 }}>
          <Form.Item name="new_password" label="New Password" rules={[{ required: true, min: 6 }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="confirm" label="Confirm Password"
            dependencies={['new_password']}
            rules={[
              { required: true },
              ({ getFieldValue }) => ({
                validator(_, v) {
                  return !v || getFieldValue('new_password') === v ? Promise.resolve() : Promise.reject('Passwords do not match');
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => setPwdModal(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" style={{ background: '#C41E3A' }}>Reset Password</Button>
          </Space>
        </Form>
      </Modal>
    </div>
  );
}
